import { useCallback, useEffect, useRef, useState } from 'react';
import { ModuleDto } from './dtos';
import { draftRecoveryStore, recoveryId } from './draft-recovery-store';

export interface ModuleRecoverySession { owner: string; recordId: string; baseUpdatedAt: string; copyId?: string }

export function useModuleDraftRecovery(value: ModuleDto, baseline: ModuleDto, session: ModuleRecoverySession | undefined,
    flush: () => void) {
    const latest = useRef({ value, flush }); latest.current = { value, flush };
    const [status, setStatus] = useState('');
    const baselineText = useRef(JSON.stringify(baseline));
    const stopped = useRef(false);
    const queue = useRef(Promise.resolve());
    const lastText = useRef('');
    const lastValue = useRef<ModuleDto | null>(null);
    const mounted = useRef(true);
    const reportFailure = useCallback(() => {
        if (mounted.current) setStatus('Draft recovery is unavailable. Keep this editor open until you save.');
    }, []);
    const clear = useCallback(async () => {
        stopped.current = true;
        if (!session) return;
        // Queue deletion after any in-flight write so an old copy cannot reappear after Save/Cancel.
        queue.current = queue.current.then(() => draftRecoveryStore.remove(recoveryId(session.owner, session.recordId, session.copyId))).catch(reportFailure);
        await queue.current;
    }, [session, reportFailure]);
    useEffect(() => {
        if (!session) return;
        mounted.current = true; stopped.current = false;
        const capture = () => {
            if (stopped.current) return;
            latest.current.flush();
            const module = latest.current.value;
            if (module === lastValue.current && lastText.current) return;
            lastValue.current = module;
            const serialized = JSON.stringify(module);
            if (serialized === lastText.current) return;
            const hasImportedContent = !module.module_key && !!module.fragments?.length;
            const id = recoveryId(session.owner, session.recordId, session.copyId);
            if (serialized === baselineText.current && !hasImportedContent) {
                queue.current = queue.current.then(() => draftRecoveryStore.remove(id)).catch(reportFailure);
            } else {
                const draft = { id, ...session, version: 1 as const, savedAt: new Date().toISOString(), module: JSON.parse(serialized) };
                queue.current = queue.current.then(() => draftRecoveryStore.put(draft)).then(() => {
                    if (mounted.current) setStatus(`Recovery copy saved at ${new Date(draft.savedAt).toLocaleTimeString()}. Stored in this browser only.`);
                }).catch(() => { lastText.current = ''; reportFailure(); });
            }
            lastText.current = serialized;
        };
        let timer: ReturnType<typeof setTimeout>;
        const schedule = () => { clearTimeout(timer); timer = setTimeout(capture, 750); };
        const onHidden = () => { if (document.visibilityState === 'hidden') capture(); };
        // Input covers blur-only fields; the interval also catches menu actions and fragment edits.
        window.addEventListener('input', schedule);
        document.addEventListener('visibilitychange', onHidden);
        window.addEventListener('pagehide', capture);
        const interval = setInterval(capture, 1500);
        schedule();
        return () => {
            mounted.current = false;
            clearTimeout(timer); clearInterval(interval);
            window.removeEventListener('input', schedule);
            document.removeEventListener('visibilitychange', onHidden);
            window.removeEventListener('pagehide', capture);
        };
    }, [session, reportFailure]);
    const resume = useCallback((savedBaseline?: ModuleDto) => {
        if (savedBaseline) baselineText.current = JSON.stringify(savedBaseline);
        stopped.current = false;
        lastText.current = '';
        lastValue.current = null;
        setStatus('');
    }, []);
    return { clear, resume, status };
}
